import React from 'react';
import {
  Menu,
  Search,
  User,
  Settings,
  LogOut,
  HelpCircle,
  Moon,
  Sun,
} from 'lucide-react';
import {
  cn,
  Avatar,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@mellify/ui';
import { useNavigate, Link } from 'react-router-dom';
import { useSidebarStore } from '../../stores/sidebarStore';
import { useAuthStore } from '../../stores/authStore';
import { ROUTES } from '../../routes/routeConfig';
import { NotificationBell } from '../../pages/dashboard/components/NotificationBell';
import { MellifyLogo } from '../public/MellifyLogo';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentMemberRole } from '../../hooks/useStoreSelectors';

export interface TopNavbarProps {
  /** Additional className */
  className?: string;
}

export const TopNavbar: React.FC<TopNavbarProps> = ({ className }) => {
  const { openMobile } = useSidebarStore();
  const navigate = useNavigate();
  const { user } = useAuth();
  const logout = useAuthStore((state) => state.logout);
  const memberRole = useCurrentMemberRole();
  const [isDark, setIsDark] = React.useState(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false
  );

  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim()
    : 'User';

  const displayRole = memberRole
    ? memberRole.charAt(0).toUpperCase() + memberRole.slice(1).toLowerCase()
    : null;

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    if (newIsDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleSignOut = () => {
    logout();
    navigate(ROUTES.SIGN_IN);
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-40 flex items-center gap-4 h-16 px-4 lg:px-6 bg-white dark:bg-dark-900 border-b border-dark-200 dark:border-dark-700',
        className
      )}
    >
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={openMobile}
        className="inline-flex items-center justify-center p-2 rounded-lg text-dark-500 dark:text-dark-400 hover:bg-dark-100 dark:hover:bg-dark-700 hover:text-dark-700 dark:hover:text-dark-200 transition-colors lg:hidden outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-label="Open navigation menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Logo — visible on mobile when sidebar is hidden */}
      <Link
        to={ROUTES.DASHBOARD}
        className="flex items-center no-underline lg:hidden"
        aria-label="Mellify Dashboard"
      >
        <MellifyLogo size="sm" showText={false} />
      </Link>

      {/* Spacer to push everything to the right */}
      <div className="flex-1" />

      {/* Search bar placeholder */}
      <div className="max-w-lg w-full">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 dark:text-dark-500" />
          <input
            type="text"
            placeholder="Search posts, campaigns, contacts..."
            className="w-full h-9 pl-9 pr-3 text-sm bg-dark-50 dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-lg text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 transition-colors"
            aria-label="Search"
          />
          <kbd className="hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5 rounded border border-dark-200 dark:border-dark-600 bg-white dark:bg-dark-700 px-1.5 py-0.5 text-[10px] font-medium text-dark-400 dark:text-dark-500">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-1">
        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className="inline-flex items-center justify-center p-2 rounded-lg text-dark-500 dark:text-dark-400 hover:bg-dark-100 dark:hover:bg-dark-700 hover:text-dark-700 dark:hover:text-dark-200 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Notification bell with dropdown */}
        <NotificationBell />

        {/* Help */}
        <button
          type="button"
          className="hidden sm:inline-flex items-center justify-center p-2 rounded-lg text-dark-500 dark:text-dark-400 hover:bg-dark-100 dark:hover:bg-dark-700 hover:text-dark-700 dark:hover:text-dark-200 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label="Help"
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        {/* Divider */}
        <div className="hidden sm:block w-px h-6 bg-dark-200 dark:bg-dark-700 mx-1" />

        {/* User avatar dropdown */}
        <DropdownMenu
          trigger={
            <button
              type="button"
              className="flex items-center gap-2 p-1 rounded-lg hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="User menu"
            >
              <Avatar
                name={displayName}
                src={user?.avatarUrl ?? undefined}
                size="sm"
              />
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-dark-900 dark:text-dark-100 leading-tight">
                  {displayName}
                </p>
                {displayRole && (
                  <p className="text-xs text-dark-500 dark:text-dark-400 leading-tight">
                    {displayRole}
                  </p>
                )}
              </div>
            </button>
          }
          align="end"
          side="bottom"
        >
          <DropdownMenuLabel>My Account</DropdownMenuLabel>
          <DropdownMenuItem
            icon={<User className="w-4 h-4" />}
            onClick={() => navigate(ROUTES.DASHBOARD_PROFILE)}
          >
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Settings className="w-4 h-4" />}
            onClick={() => navigate(ROUTES.DASHBOARD_SETTINGS)}
          >
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem icon={<HelpCircle className="w-4 h-4" />}>
            Help & Support
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            icon={<LogOut className="w-4 h-4" />}
            danger
            onClick={handleSignOut}
          >
            Sign Out
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
    </header>
  );
};

TopNavbar.displayName = 'TopNavbar';